import classNames from 'classnames';
import get from 'lodash/get';
import React, { type CSSProperties, memo, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { EMPTY_ARRAY, EMPTY_OBJ } from '../../constants';
import { COLOR, SIZE } from '../../styles';
import { Button } from '../Button';
import { PopupMenu } from '../PopupMenu';
import { type ButtonMenuProps } from './ButtonMenu.types';

const ButtonMenu: React.FC<ButtonMenuProps> = memo(
  ({
    isVisible = true,
    onMenuClose,
    onMenuOpen,
    buttonAttributes,
    buttonBackIcon,
    buttonClass = '',
    buttonColorMode = 'light',
    buttonIcon,
    buttonIconOnly = false,
    buttonIconPosition = 'front',
    buttonSize,
    buttonStyle = EMPTY_OBJ,
    buttonTextStyle = EMPTY_OBJ,
    buttonTooltip = '',
    buttonType,
    buttonTypography,
    className,
    colorMode,
    dataTestId = 'button-menu',
    dataIntercomTarget,
    disabled = false,
    id,
    label,
    menuClass = '',
    menuItemHandlerData,
    menuItems = EMPTY_ARRAY,
    menuItemVisibilityHandler,
    menuStyle = EMPTY_OBJ,
    menuRootClass = '',
    tooltipHorizontalOffset = 0,
    tooltipVerticalOffset = 0,
    showPointer = true,
    trigger,
  }) => {
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const buttonRef = useRef<HTMLElement | null>(null);
    const popupEvent = new Event('unique-popup');
    const [autoFocus, setAutoFocus] = useState(true);
    const [menuVisible, setMenuVisible] = useState(false);
    const [hasMounted, setHasMounted] = useState(false);

    const closeMenu = useCallback(
      (event?: any) => {
        // don't close menu if clicking one of the menu item
        const isWrapperMissing =
          !wrapperRef || !get(event, 'target') || !wrapperRef.current?.contains(event.target);
        if (hasMounted && isWrapperMissing) {
          document.removeEventListener('click', closeMenu);
          document.removeEventListener('unique-popup', closeMenu);
          setMenuVisible(false);
          if (onMenuClose) {
            onMenuClose();
          }
        } else {
          document.addEventListener('unique-popup', closeMenu, { once: true });
          document.addEventListener('click', closeMenu, { once: true });
        }
      },
      [hasMounted, onMenuClose],
    );

    useEffect(() => {
      setHasMounted(true);
      return () => setHasMounted(false);
    }, []);

    useEffect(() => {
      if (isVisible) {
        closeMenu();
      }
      // only trigger effect if isVisible is true
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVisible]);

    // eslint-disable-next-line
    // @ts-ignore
    const toggleMenu = (event) => {
      event.stopPropagation();
      event.preventDefault();

      if (!menuVisible) {
        document.dispatchEvent(popupEvent);
        document.addEventListener('unique-popup', closeMenu, { once: true });
        document.addEventListener('click', closeMenu, { once: true });
        if (onMenuOpen) {
          onMenuOpen();
        }
      } else if (onMenuClose) {
        onMenuClose();
      }

      if (hasMounted) {
        setAutoFocus(event.type === 'click' ? false : true);
        setMenuVisible(!menuVisible);
      }
    };

    const setButtonRef = useCallback((node: HTMLElement | null) => {
      buttonRef.current = node;
    }, []);

    const buttonMenuWrapperClass = classNames(
      'button-menu-wrapper',
      className,
      `mode-${colorMode}`,
    );
    const buttonMenuClass = classNames('button-menu', menuClass);

    const wrapperStyle: CSSProperties = {};
    if (menuVisible) {
      // when the menu is open, leave it visible until it's closed
      wrapperStyle.display = 'block';
    }

    return (
      <Wrapper
        className={buttonMenuWrapperClass}
        ref={wrapperRef}
        style={wrapperStyle}
        data-menu-open={menuVisible || undefined}
      >
        {trigger ? (
          <div ref={setButtonRef} onClick={toggleMenu} data-testid={dataTestId}>
            {trigger}
          </div>
        ) : (
          <Button
            attributes={buttonAttributes}
            backIcon={buttonBackIcon}
            className={`button-menu-button ${buttonClass}`}
            colorMode={buttonColorMode || colorMode}
            disabled={disabled}
            dataTestId={dataTestId || dataIntercomTarget}
            dataIntercomTarget={dataIntercomTarget}
            icon={buttonIcon}
            iconOnly={buttonIconOnly}
            iconPosition={buttonIconPosition}
            id={id}
            onClick={toggleMenu}
            refFunc={(button) => setButtonRef(button)}
            size={buttonSize}
            style={buttonStyle}
            text={label}
            textStyle={buttonTextStyle}
            tooltip={buttonTooltip}
            type={buttonType}
            typography={buttonTypography}
          />
        )}
        {menuVisible && (
          <PopupMenu
            autoFocus={autoFocus}
            className={buttonMenuClass}
            closeMenu={closeMenu}
            colorMode={colorMode}
            dataTestId={dataTestId}
            handlerData={menuItemHandlerData}
            horizontalOffset={tooltipHorizontalOffset}
            verticalOffset={tooltipVerticalOffset}
            isPopupOpen={menuVisible}
            menuItems={menuItems}
            parent={buttonRef.current}
            style={menuStyle}
            rootClass={menuRootClass}
            visibilityHandler={menuItemVisibilityHandler}
            showPointer={showPointer}
          />
        )}
      </Wrapper>
    );
  },
);

// Styled Components
const Wrapper = styled.div`
  position: relative;

  &.mode-dark {
    .button-menu-button {
      .icon {
        color: ${COLOR['white']};
      }

      &:hover,
      &:focus {
        .icon {
          color: ${COLOR['turquoise-darker']};
        }
      }
    }
  }

  button.styled {
    min-height: ${SIZE['28']};
    min-width: ${SIZE['28']};

    &.clear.button-menu-button {
      border-radius: 50%;
      margin: 0;
      padding: 0;
    }

    svg.icon {
      margin-right: initial;
    }

    .button-menu {
      top: calc(100% + 2px);
      right: calc(50% - 18px);

      &.top {
        top: auto;
        bottom: calc(100% + 4px);
      }
    }
  }
`;

// Display Name
ButtonMenu.displayName = 'ButtonMenu';

export default ButtonMenu;
